/* ============================================================
 * a11y-drawer-focus.js
 * Accessibility remediation - Converge audit 2026-05-22, finding #37 (Blocking),
 * with partial cover for #35 and #50. Added 2026-07-30.
 *
 * THE PROBLEM (two halves)
 *
 * 1. The drawer's close control is unreachable by keyboard. In
 *    sections/page-header.liquid the markup is:
 *
 *        <div class="wt-drawer__close" tabindex="-1" role="button" ...>
 *
 *    tabindex="-1" removes it from the tab order permanently. A keyboard user
 *    can open the drawer and tab through the menu, but cannot reach the control
 *    that closes it. That is the keyboard trap the audit reports at 400% zoom.
 *
 * 2. The drawer is always present in the DOM. Below 1200px it is moved
 *    off-canvas with a transform, but nothing marks it inert or aria-hidden, so
 *    focus can travel into a menu the user cannot see.
 *
 * WHY THIS IS JAVASCRIPT AND NOT LIQUID
 * Both fixes are one-attribute changes to sections/page-header.liquid, but the
 * write API replaces whole files and that file is 33 KB of Liquid. Doing it at
 * runtime avoids retransmitting it. A developer should eventually make change 1
 * directly in the markup (tabindex="-1" -> tabindex="0") and delete that part
 * of this script.
 *
 * DELIBERATELY NOT A FOCUS TRAP
 * A modal dialog would normally cycle focus inside itself. The audit's complaint
 * is the opposite - that focus cannot get OUT - so this script makes the exit
 * reachable (focusable close control, plus Escape) rather than adding cycling,
 * which would risk making the trap worse if the open/closed detection is wrong.
 * ============================================================ */

(function () {
  "use strict";

  var DRAWER_ID = "wt-drawer-nav";
  var drawer = document.getElementById(DRAWER_ID);
  if (!drawer) return;

  var closeBtn = drawer.querySelector(".wt-drawer__close");
  var lastTrigger = null;

  function triggers() {
    return document.querySelectorAll(
      '[aria-controls="' + DRAWER_ID + '"], .wt-header__menu-trigger, .wt-header__sticky-menu-trigger'
    );
  }

  /* ---- 1. make the close control reachable -------------------------------- */
  if (closeBtn) {
    closeBtn.setAttribute("tabindex", "0");
    // role="button" on a <div> does not get Enter/Space activation for free.
    closeBtn.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        closeBtn.click();
      }
    });
  }

  /* ---- 2. open/closed detection ------------------------------------------
     Deliberately geometric rather than class-based. At >=1200px the theme's
     own custom CSS turns the drawer into the inline desktop navigation
     (position: relative; transform: unset), so it is genuinely on-screen and
     must stay reachable. Below that it is translated off-canvas when closed.
     Measuring position handles both without needing to know class names, which
     matters because a class rename would silently break a class-based check
     and could leave the desktop nav inert. */
  function offscreen(el) {
    var r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return true;
    return (
      r.right <= 1 ||
      r.left >= window.innerWidth - 1 ||
      r.bottom <= 1 ||
      r.top >= window.innerHeight - 1
    );
  }

  function isOpen() {
    var cs = window.getComputedStyle(drawer);
    if (cs.display === "none" || cs.visibility === "hidden") return false;
    return !offscreen(drawer);
  }

  /* ---- 3. keep the drawer out of the tab order while it is off-canvas ----- */
  var syncing = false;

  function sync() {
    if (syncing) return;
    syncing = true;
    try {
      var open = isOpen();

      if (open) {
        drawer.removeAttribute("inert");
        drawer.removeAttribute("aria-hidden");
      } else {
        // Never inert a container that currently holds focus - that would
        // strand the user with no focused element at all.
        if (drawer.contains(document.activeElement)) {
          restoreFocus();
        }
        drawer.setAttribute("inert", "");
        drawer.setAttribute("aria-hidden", "true");
      }

      var t = triggers();
      for (var i = 0; i < t.length; i++) {
        if (t[i].hasAttribute("aria-expanded")) {
          t[i].setAttribute("aria-expanded", open ? "true" : "false");
        }
      }
    } catch (e) {
      // Never let this break the header. Failing open is safer than failing
      // closed: worst case the pre-existing behaviour returns.
      drawer.removeAttribute("inert");
      drawer.removeAttribute("aria-hidden");
      if (window.console && console.warn) console.warn("a11y-drawer-focus:", e);
    }
    syncing = false;
  }

  function restoreFocus() {
    var target = lastTrigger;
    if (!target || !target.isConnected || offscreen(target)) {
      var t = triggers();
      for (var i = 0; i < t.length; i++) {
        if (!offscreen(t[i])) { target = t[i]; break; }
      }
    }
    if (target && typeof target.focus === "function") {
      target.focus();
    }
  }

  /* ---- 4. Escape closes, and focus returns to the trigger ----------------- */
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape" && e.key !== "Esc") return;
    if (!isOpen()) return;
    if (!drawer.contains(document.activeElement) && document.activeElement !== document.body) {
      // Escape pressed while focus is elsewhere - leave other components alone.
      return;
    }
    if (closeBtn) closeBtn.click();
    restoreFocus();
    window.setTimeout(sync, 50);
  });

  /* ---- 5. remember which trigger opened it ------------------------------- */
  document.addEventListener(
    "click",
    function (e) {
      var t = e.target.closest
        ? e.target.closest('[aria-controls="' + DRAWER_ID + '"], .wt-header__menu-trigger, .wt-header__sticky-menu-trigger')
        : null;
      if (t) {
        lastTrigger = t;
        window.setTimeout(sync, 50);
        window.setTimeout(sync, 400); // after the CSS transition settles
      }
    },
    true
  );

  /* ---- 6. resync on anything that can change the drawer's position ------- */
  drawer.addEventListener("transitionend", sync);
  window.addEventListener("resize", sync);
  window.addEventListener("orientationchange", sync);

  if (window.MutationObserver) {
    new MutationObserver(function () {
      window.setTimeout(sync, 0);
    }).observe(drawer, { attributes: true, attributeFilter: ["class", "style", "data-open"] });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", sync);
  } else {
    sync();
  }
})();
